import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState } from 'dashboard/components/common';
import LowStockKpiRow  from './LowStockKpiRow';
import LowStockToolbar from './LowStockToolbar';
import LowStockTable   from './LowStockTable';

/**
 * Low-stock report.
 *
 * Shows per-variant available stock vs the threshold the owner set.
 * Inline threshold edits accumulate in local `edits` state until the
 * user hits Save — one batched round-trip rather than one PATCH per
 * keystroke.
 */
export default function LowStock() {
  usePageTitle('Low stock', 'Products running out, so you can reorder before they go.');
  const [selectedBrand] = useOutletContext();
  const [data, setData] = useState({ rows: [], counts: { out: 0, low: 0, ok: 0, untracked: 0 } });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('low_out');
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({}); // variant_id -> new threshold
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.getShopLowStock({ brand_id: selectedBrand })
      .then((res) => {
        setData(res || { rows: [], counts: { out: 0, low: 0, ok: 0, untracked: 0 } });
        setEdits({});
      })
      .catch((err) => toast.error(err?.message || 'Failed to load low-stock data', { toastId: 'lowstock-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getShopLowStock({ brand_id: selectedBrand })
      .then((res) => {
        if (cancelled) return;
        setData(res || { rows: [], counts: { out: 0, low: 0, ok: 0, untracked: 0 } });
        setEdits({});
      })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load low-stock data', { toastId: 'lowstock-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (filter === 'out'     && r.status !== 'out') return false;
      if (filter === 'low'     && r.status !== 'low') return false;
      if (filter === 'low_out' && r.status === 'ok')  return false;
      if (filter === 'ok'      && r.status !== 'ok')  return false;
      if (!q) return true;
      return (
        (r.product_name || '').toLowerCase().includes(q) ||
        (r.variant_name || '').toLowerCase().includes(q) ||
        (r.sku          || '').toLowerCase().includes(q)
      );
    });
  }, [data, filter, search]);

  const dirtyCount = Object.keys(edits).length;
  const setEdit = (id, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      if (value === '' || value == null) delete next[id];
      else next[id] = Math.max(0, Math.floor(Number(value) || 0));
      return next;
    });
  };

  const saveAll = async () => {
    const updates = Object.entries(edits).map(([variant_id, low_stock_threshold]) => ({
      variant_id, low_stock_threshold,
    }));
    if (updates.length === 0) return;
    setSaving(true);
    try {
      const res = await API.bulkSetShopThresholds({ brand_id: selectedBrand, updates });
      const saved = res?.saved || 0;
      toast.success(`${saved} threshold${saved === 1 ? '' : 's'} updated`);
      if (res?.errors?.length) toast.warn(`${res.errors.length} row(s) failed`);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <Stack spacing='16px'>
        <Box>
          <Typography variant='h6' fontWeight='700'>Low stock</Typography>
          <Typography variant='body2' color='text.secondary'>
            Variants whose available inventory is at or below the threshold you set. Edit a threshold inline; we save the lot when you click Save.
          </Typography>
        </Box>

        {!selectedBrand && <EmptyState>Select a brand.</EmptyState>}

        {selectedBrand && (
          <>
            <LowStockKpiRow counts={data.counts} />
            <Paper
              elevation={0}
              sx={{ borderRadius: '16px', p: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}
            >
              <LowStockToolbar
                filter={filter}
                onFilterChange={setFilter}
                search={search}
                onSearchChange={setSearch}
                dirtyCount={dirtyCount}
                saving={saving}
                onDiscard={() => setEdits({})}
                onSave={saveAll}
              />
              <LowStockTable
                rows={filteredRows}
                loading={loading}
                edits={edits}
                onSetEdit={setEdit}
              />
            </Paper>
          </>
        )}
      </Stack>
    </Box>
  );
}
